/**
 * Email Deliverability Background Storage
 *
 * Persiste en localStorage la lista de identidades que el usuario "envio a
 * segundo plano" desde el wizard de conexion (Step 4 estado verifying).
 *
 * Patron tipo `BackgroundUploadContext` de orbit: el usuario cierra el
 * modal pero el progreso sigue siendo visible en un panel flotante abajo
 * a la derecha. Cuando el WebSocket emite `sending_identity_status` con
 * verified/failed, el panel actualiza el estado y queda visible hasta
 * que el usuario lo descarte manualmente con la X.
 *
 * NO usamos las identidades en pending/verifying directamente del listado
 * de la API (eso traeria identities legadas que el user nunca toco). Solo
 * trackeamos las que el user proactivamente metio al background desde el
 * wizard.
 */
import type {
  SendingIdentityDetail,
  SendingIdentityStatus,
} from '@/services/api/sendingIdentitiesService';

const STORAGE_KEY = 'owly_email_deliverability_background';
const STORAGE_VERSION = 1;
const MAX_AGE_DAYS = 7;
const MS_PER_DAY = 24 * 60 * 60 * 1000;

export interface BackgroundIdentity {
  id: string;
  from_email: string;
  domain: string;
  company_id: string | null;
  status: SendingIdentityStatus;
  is_managed_by_owly: boolean;
  /** ISO timestamp de cuando se metio a background. */
  added_at: string;
  /** ISO timestamp de cuando llego a estado terminal (verified/failed). null mientras verifying. */
  terminal_at: string | null;
}

interface StoredEnvelope {
  v: number;
  items: BackgroundIdentity[];
}

function readStored(): BackgroundIdentity[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as StoredEnvelope;
    if (parsed.v !== STORAGE_VERSION || !Array.isArray(parsed.items)) return [];
    return parsed.items;
  } catch {
    return [];
  }
}

/**
 * Evento custom para notificar a TODOS los hooks `useEmailDeliverabilityBackground`
 * de la misma tab cuando alguien escribe en el storage. El `storage` event
 * nativo de browser solo dispara entre tabs distintas, NO en la misma tab —
 * por eso necesitamos un canal propio para que el wizard que llama `add` y
 * el indicator que esta montado en AppRouter (misma tab) se sincronicen.
 */
export const BACKGROUND_CHANGED_EVENT = 'owly-email-deliverability-bg-changed';

function emitChanged(): void {
  try {
    window.dispatchEvent(new Event(BACKGROUND_CHANGED_EVENT));
  } catch {
    // SSR / non-browser env — ignorar.
  }
}

function writeStored(items: BackgroundIdentity[]): void {
  try {
    const envelope: StoredEnvelope = { v: STORAGE_VERSION, items };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(envelope));
    emitChanged();
  } catch {
    // Si localStorage esta lleno o no esta disponible, ignoramos. No es
    // critico — el indicator solo no se muestra hasta el siguiente WS event.
  }
}

/**
 * Limpia entradas viejas (> MAX_AGE_DAYS desde added_at). Util para no
 * acumular indefinidamente en localStorage si el user nunca cierra una.
 */
function pruneStale(items: BackgroundIdentity[]): BackgroundIdentity[] {
  const now = Date.now();
  return items.filter((item) => {
    const ageMs = now - new Date(item.added_at).getTime();
    return ageMs < MAX_AGE_DAYS * MS_PER_DAY;
  });
}

export function listBackgroundIdentities(): BackgroundIdentity[] {
  return pruneStale(readStored());
}

export function addBackgroundIdentity(identity: SendingIdentityDetail): BackgroundIdentity[] {
  const items = pruneStale(readStored());
  const exists = items.find((i) => i.id === identity.id);
  if (exists) return items;
  const entry: BackgroundIdentity = {
    id: identity.id,
    from_email: identity.from_email,
    domain: identity.domain,
    company_id: identity.company,
    status: identity.verification_status,
    is_managed_by_owly: identity.is_managed_by_owly,
    added_at: new Date().toISOString(),
    terminal_at: null,
  };
  const next = [...items, entry];
  writeStored(next);
  return next;
}

export function removeBackgroundIdentity(identityId: string): BackgroundIdentity[] {
  const items = readStored().filter((i) => i.id !== identityId);
  writeStored(items);
  return items;
}

export function clearTerminalBackgroundIdentities(): BackgroundIdentity[] {
  const items = readStored().filter(
    (i) => i.status !== 'verified' && i.status !== 'failed' && i.status !== 'disabled',
  );
  writeStored(items);
  return items;
}

/**
 * Actualiza el status de una identity en background. Llamado desde el hook
 * cuando llega un evento WS o cuando se hace refetch del detail. Si el nuevo
 * status es terminal y no tiene terminal_at, lo setea.
 */
export function updateBackgroundIdentityStatus(
  identityId: string,
  status: SendingIdentityStatus,
): BackgroundIdentity[] {
  const items = readStored();
  const idx = items.findIndex((i) => i.id === identityId);
  if (idx === -1) return items;
  const isTerminal = status === 'verified' || status === 'failed' || status === 'disabled';
  const updated: BackgroundIdentity = {
    ...items[idx],
    status,
    terminal_at:
      isTerminal && !items[idx].terminal_at
        ? new Date().toISOString()
        : items[idx].terminal_at,
  };
  const next = [...items];
  next[idx] = updated;
  writeStored(next);
  return next;
}

export const STORAGE_EVENT_KEY = STORAGE_KEY;
